import { useForm } from '@tanstack/react-form'
import { useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { toast } from 'sonner'
import { useCurrentBusiness } from '~/entities/business/model/use-business'
import { listingImagesApi } from '~/entities/listing/api'
import { useCreateListing, usePublishListing } from '~/entities/listing/model/use-listings'
import { ListingContactSection } from '~/features/listings/components/listing-contact-section'
import { ListingDetailsSection } from '~/features/listings/components/listing-details-section'
import { ListingHoursSection } from '~/features/listings/components/listing-hours-section'
import { ListingImagesSection } from '~/features/listings/components/listing-images-section'
import { ListingLocationSection } from '~/features/listings/components/listing-location-section'
import { ListingPreviewModal } from '~/features/listings/components/listing-preview-modal'
import type { PlaceSaveOutcome } from '~/features/listings/website-start/place-website-flow'
import { GuardedActionButton } from '~/features/shared/components/guarded-action-button'
import type { ImageFile } from '~/features/shared/components/images-section'
import { useAfterSaveAction } from '~/features/shared/hooks/use-after-save-action'
import * as m from '~/paraglide/messages'
import { getErrorMessageFromError } from '~/shared/lib/api-errors'
import { log } from '~/shared/lib/logger'
import { QueryKey, queryKey } from '~/shared/lib/query-keys'
import { useFormPersistence } from '~/shared/lib/use-form-persistence'
import type { PriceLevelValue } from '~/shared/types'
import { Button } from '~/shared/ui/button'
import { PageHeader } from '~/widgets/page-header'

interface CreateListingFormProps {
  onCreated: (listingId: string, outcome: PlaceSaveOutcome) => void
  onCancel: () => void
}

/** The manual form for a new place, without the website step. */
export function CreateListingForm({ onCreated, onCancel }: CreateListingFormProps) {
  const queryClient = useQueryClient()
  const { data: business } = useCurrentBusiness()
  const createListing = useCreateListing(business?.id || '')
  const publishListing = usePublishListing(business?.id || '')
  const [images, setImages] = useState<ImageFile[]>([])
  const [primaryImageIndex, setPrimaryImageIndex] = useState(0)
  const [isUploadingImages, setIsUploadingImages] = useState(false)
  const afterSave = useAfterSaveAction<'publish'>()

  const handleImagesChange = (
    newImages: ImageFile[],
    newPrimaryIndex: number,
    _newInsertPositions?: number[],
  ) => {
    setImages(newImages)
    setPrimaryImageIndex(newPrimaryIndex)
  }

  const form = useForm({
    defaultValues: {
      name: '',
      description: '',
      location: {
        name: '',
        street: '',
        city: '',
        country: 'Austria',
      },
      postalCode: '',
      priceLevel: '' as PriceLevelValue,
      phone: '',
      email: '',
      website: '',
      facebook: '',
      instagram: '',
      twitter: '',
      nicknames: '',
      hours: [
        {
          day: 'Monday',
          open: '08:00:00',
          close: '12:00:00',
        },
        {
          day: 'Monday',
          open: '13:00:00',
          close: '17:00:00',
        },
        {
          day: 'Tuesday',
          open: '08:00:00',
          close: '12:00:00',
        },
        {
          day: 'Tuesday',
          open: '13:00:00',
          close: '17:00:00',
        },
        {
          day: 'Wednesday',
          open: '08:00:00',
          close: '12:00:00',
        },
        {
          day: 'Wednesday',
          open: '13:00:00',
          close: '17:00:00',
        },
      ],
    },
    onSubmit: async ({ value }) => {
      const afterSaveAction = afterSave.consume()

      try {
        const createdListing = await createListing.mutateAsync(
          value as Parameters<typeof createListing.mutateAsync>[0],
        )

        if (images.length > 0 && createdListing.id) {
          setIsUploadingImages(true)
          try {
            const files = images.map((img) => img.file)
            await listingImagesApi.uploadMultiple(createdListing.id, files)

            toast.success(m.listingCreatedWithImages(), {
              description: m.yourListingAndImagesHaveBeenUploadedSuccessfully(),
            })

            await queryClient.invalidateQueries({
              queryKey: queryKey(QueryKey.LISTINGS, business?.id),
            })
          } catch (imageError) {
            log.error(
              imageError instanceof Error ? imageError : new Error(String(imageError)),
              'Failed to upload images',
            )
            toast.warning(m.listingCreatedButSomeImagesFailedToUpload(), {
              description: m.youCanAddImagesLaterFromTheListingDetailsPage(),
            })
          } finally {
            setIsUploadingImages(false)
          }
        } else {
          toast.success(m.listingCreated(), {
            description: m.yourListingHasBeenCreatedSuccessfully(),
          })
        }

        clearFormState()

        if (afterSaveAction === 'publish' && createdListing.id) {
          try {
            await publishListing.mutateAsync(createdListing.id)
            toast.success(m.listingPublished())
            onCreated(createdListing.id, 'published')
          } catch (publishError) {
            log.error(
              publishError instanceof Error ? publishError : new Error(String(publishError)),
              '[CreateListing] Failed to publish after create',
            )
            toast.error(m.failedToPublishListing(), {
              description: getErrorMessageFromError(publishError),
            })
            onCreated(createdListing.id, 'publishFailed')
          }
        } else {
          onCreated(createdListing.id, 'saved')
        }
      } catch (error) {
        toast.error(getErrorMessageFromError(error))
      }
    },
  })

  const { clearFormState, hasSavedState } = useFormPersistence(form, {
    storageKey: 'pulsify-create-listing-draft',
  })

  const handleCancel = () => {
    clearFormState()
    onCancel()
  }

  const handleClearDraft = () => {
    clearFormState()
    form.reset()
    toast.info(m.draftCleared(), {
      description: m.yourDraftHasBeenClearedSuccessfully(),
    })
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title={m.createListing()}
        description={m.addNewListingForYourBusiness()}
        action={
          <div className="flex flex-wrap gap-2">
            <form.Subscribe
              selector={(state) => ({
                name: state.values.name,
                description: state.values.description,
                location: state.values.location,
                phone: state.values.phone,
              })}
            >
              {({ name, description, location, phone }) => (
                <ListingPreviewModal
                  name={name}
                  description={description}
                  location={location}
                  phone={phone}
                  coverImageUrl={images.length > 0 ? images[primaryImageIndex]?.preview : ''}
                />
              )}
            </form.Subscribe>
            {hasSavedState() && (
              <Button variant="ghost" size="sm" onClick={handleClearDraft}>
                {m.clearDraft()}
              </Button>
            )}
            <Button variant="outline" onClick={handleCancel}>
              {m.cancel()}
            </Button>
          </div>
        }
      />
      <form
        onSubmit={(e) => {
          e.preventDefault()
          e.stopPropagation()
          form.handleSubmit()
        }}
      >
        <div className="mb-6 grid gap-6 lg:grid-cols-2 lg:items-start">
          <div className="min-w-0">
            <ListingDetailsSection form={form} />
          </div>
          <div className="min-w-0 space-y-6">
            <ListingImagesSection onImagesChange={handleImagesChange} />
            <ListingLocationSection form={form} />
            <ListingContactSection form={form} />
          </div>
        </div>

        <div className="mb-6">
          <ListingHoursSection form={form} bufferKey="create" />
        </div>

        <form.Subscribe
          selector={(state) => ({
            canSubmit: state.canSubmit,
            isSubmitting: state.isSubmitting,
          })}
        >
          {({ canSubmit, isSubmitting }) => {
            const isDisabled =
              !canSubmit || isSubmitting || createListing.isPending || isUploadingImages
            const isBusy = createListing.isPending || isSubmitting
            return (
              <div className="flex flex-wrap items-center gap-4">
                <Button type="submit" disabled={isDisabled}>
                  {isBusy && !afterSave.action
                    ? m.creating()
                    : isUploadingImages
                      ? m.uploadingImages()
                      : m.createListing()}
                </Button>
                <GuardedActionButton
                  enabled={images.length > 0}
                  hint={m.addACoverImageToPublish()}
                  label={m.saveAndPublish()}
                  busyLabel={m.savingAndPublishing()}
                  busy={isBusy && afterSave.action === 'publish'}
                  disabled={isDisabled}
                  variant="secondary"
                  onClick={() => afterSave.trigger(form, 'publish')}
                />
                <Button
                  type="button"
                  variant="outline"
                  className="sm:ml-auto"
                  onClick={handleCancel}
                >
                  {m.cancel()}
                </Button>
              </div>
            )
          }}
        </form.Subscribe>
      </form>
    </div>
  )
}
